import { HomeLocationColored, LocationOutlined } from '@teko/ds-icon';
import {
  Button,
  Flex,
  Form,
  Input,
  Row,
  Shape,
  styled,
  useTheme,
} from '@teko/ds-mobile';
import { Select } from '@teko/ecommerce/mobile';
import { ThemeProps } from '@teko/ui';
import { Address } from '@teko/user';
import { AddressForm as AddressBookForm } from 'components/mobile/organisms';
import { useDistanceTerminalContext } from 'contexts';
import { tSelectPlaceholder } from 'helpers/translate';
import { useLocationsV2, useSuggestionAddress, useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { useState } from 'react';
import { useGetDistanceTerminal } from '../useGetDistanceTerminal';
import { SwitchGetByGeoLocation } from './SwitchGetByGeoLocation';

interface AddressForm {
  address: string;
  provinceCode: string;
  wardCode: string;
  districtCode?: string;
}
const COUNT_STRING_WITHOUT_STREET_ADDRESS = 4;

export const ModeEnterAddress = () => {
  const theme = useTheme();
  const [form] = Form.useForm<AddressForm>();
  const [missingRequiredField, setMissingRequiredField] = useState({
    province: '',
    ward: '',
    streetAddress: '',
  });
  const [place, setPlace] = useState<{ lat?: number; long?: number }>({
    lat: undefined,
    long: undefined,
  });
  const {
    isLoadingGetDistanceTerminals,
    messageError,
    isGetCurrentLocation,
    editingAddress,
    setEditingAddress,
    overrideLocation,
  } = useDistanceTerminalContext();
  const { getDistanceTerminalsByGeoLocation, getDistanceTerminalsByAddress } =
    useGetDistanceTerminal();
  const { epicOmni1825SuggestAddress } = useTenantConfig();
  const {
    provinces = [],
    wards = [],
    province,
    ward,
    onProvinceChanged,
    onWardChanged,
  } = useLocationsV2();

  const getDistanceTerminalsByAddressForm = async () => {
    const formData = await form.validateFields();
    if (!province?.code || !ward?.code || !formData.address) {
      return setMissingRequiredField({
        province: !province?.code
          ? t('Tỉnh/Thành phố không được bỏ trống')
          : '',
        ward: !ward?.code ? t('Phường/Xã không được bỏ trống') : '',
        streetAddress: !formData.address
          ? t('Địa chỉ chi tiết không được bỏ trống')
          : '',
      });
    }
    const provinceName =
      provinces.find((p) => p.code === province?.code)?.title || '';

    const wardName = wards.find((w) => w.code === ward.code)?.title || '';
    getDistanceTerminalsByAddress({
      ...formData,
      provinceName,
      wardName,
      provinceCode: province.code,
      wardCode: ward.code,
      lat: place.lat,
      long: place.long,
    });
  };
  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    isLoadingSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: province?.title || '',
    ward: ward?.title || '',
  });

  const onClickSuggest = async (placeId: string) => {
    const items = await getPlaceDetails(placeId);
    if (!items || !Array.isArray(items) || !items.length) {
      return;
    }
    // items là danh sách các placeDetail và cần lấy placeDetail đầu tiên
    // trong case này chỉ gửi 1 placeID lên nên chỉ có 1 placeDetail
    const item = items?.[0];
    const address =
      item.fullAddress
        .split(',')
        ?.splice(
          0,
          item.fullAddress.split(',').length -
            COUNT_STRING_WITHOUT_STREET_ADDRESS,
        )
        ?.join(',') ||
      item.fullAddress ||
      '';
    setOpenListSuggestion(false);
    setPlace({ lat: item.lat, long: item.long });
    const newValue = {
      provinceCode: item.provinceCode || undefined,
      ...(item.wardCode ? { wardCode: item.wardCode } : {}),
    };
    form.setFieldsValue({
      address,
      ...newValue,
    });
  };

  const resetPosition = () => {
    setPlace({ lat: undefined, long: undefined });
  };

  const handleCloseEditAddress = async (addr: Address) => {
    overrideLocation?.({
      address: addr.address || '',
      provinceName: addr.provinceName || '',
      wardName: addr.wardName || '',
      provinceCode: addr.provinceCode,
      wardCode: addr.wardCode,
      districtCode: addr.districtCode,
      districtName: addr.districtName,
      addressId: addr.addressId,
      addressNote: addr.addressNote || addr.note || '',
      lat: addr.latitude ? Number(addr.latitude) : undefined,
      long: addr.longitude ? Number(addr.longitude) : undefined,
    });
    setEditingAddress?.(undefined);
    getDistanceTerminalsByAddress({
      address: addr.address || '',
      provinceName: addr.provinceName || '',
      wardName: addr.wardName || '',
      provinceCode: addr.provinceCode,
      wardCode: addr.wardCode,
      districtCode: addr.districtCode,
      districtName: addr.districtName,
      addressId: addr.addressId,
      addressNote: addr.addressNote || addr.note || '',
      lat: addr.latitude ? Number(addr.latitude) : undefined,
      long: addr.longitude ? Number(addr.longitude) : undefined,
    });
  };

  if (editingAddress) {
    return (
      <AddressBookForm
        editingAddress={editingAddress.addressId}
        onClose={handleCloseEditAddress}
      />
    );
  }

  return (
    <>
      <Row justify="start" width="100%">
        <HomeLocationColored size={20} />
        <SubTitle
          style={{
            marginLeft: 4,
            fontSize: 14,
            color: theme.colors.black,
          }}
        >
          {t('Địa chỉ nhận hàng')}:
        </SubTitle>
      </Row>
      <SwitchGetByGeoLocation />
      <StyledForm layout="vertical" form={form}>
        <Wrapper>
          <>
            <SubTitle style={{ textAlign: 'left' }}>
              {t('Hoặc nhập địa chỉ ')}
            </SubTitle>
            <StyledSelect
              title={t('Tỉnh/Thành phố')}
              wrapperBottomSheetHeight="100dvh"
              placeholder={tSelectPlaceholder('Tỉnh/Thành phố')}
              haveFilter
              popupTitle={t('Tỉnh/Thành phố')}
              selectKey="text"
              required
              disabled={isGetCurrentLocation}
              items={provinces?.map(({ title, code }) => ({
                text: title,
                value: code,
              }))}
              value={province?.title || ''}
              onSelectChange={({ value }) => {
                const location = provinces?.find((prov) => prov.code === value);
                if (location) {
                  onProvinceChanged(location.code);
                  form.setFieldsValue({
                    provinceCode: location.code,
                  });
                  resetPosition();
                }
              }}
              error={missingRequiredField.province}
            />
            <StyledSelect
              title={t('Phường/Xã')}
              wrapperBottomSheetHeight="100dvh"
              placeholder={tSelectPlaceholder('Phường/Xã')}
              haveFilter
              disabled={isGetCurrentLocation}
              popupTitle={t('Phường/Xã')}
              selectKey="text"
              items={wards?.map(({ title, code }) => ({
                text: title,
                value: code,
              }))}
              required
              value={ward?.title || ''}
              onSelectChange={({ value }) => {
                const location = wards?.find((ward) => ward.code === value);
                if (location) {
                  onWardChanged(location.code);
                  form.setFieldsValue({
                    wardCode: location.code,
                  });
                  resetPosition();
                }
              }}
              error={missingRequiredField.ward}
            />
            <Form.Item required label={t('Địa chỉ cụ thể')} name="address">
              <Input
                disabled={isGetCurrentLocation}
                placeholder={t('Số nhà, ngõ, tên đường...')}
                onChange={(text) => {
                  setSuggestInputText(text);
                  setOpenListSuggestion(true);
                }}
              />
            </Form.Item>
            {epicOmni1825SuggestAddress &&
              !!dataSuggestion &&
              !!openListSuggestion &&
              (!!dataSuggestion.length || isLoadingSuggest) && (
                <SuggestWrapper>
                  {isLoadingSuggest ? (
                    <Flex justify="center" align="center">
                      <Spinner />
                    </Flex>
                  ) : (
                    <>
                      <Text style={{ textAlign: 'left' }}>{t('Gợi ý')}</Text>
                      {dataSuggestion.map((item) => {
                        return (
                          <ItemSuggestion
                            key={item.fullAddress}
                            onClick={() => onClickSuggest(item.placeId)}
                          >
                            <LocationOutlined />
                            <Text style={{ textAlign: 'left', marginLeft: 8 }}>
                              {item.fullAddress}
                            </Text>
                          </ItemSuggestion>
                        );
                      })}
                    </>
                  )}
                </SuggestWrapper>
              )}
            <Form.Item label={t('Ghi chú')} name="addressNote" required={false}>
              <Input
                disabled={isGetCurrentLocation}
                placeholder={t('Ví dụ: Cổng màu xanh, nhà bên cạnh cây xăng')}
              />
            </Form.Item>
            {!!missingRequiredField.streetAddress && (
              <Error>{missingRequiredField.streetAddress}</Error>
            )}
          </>

          {!!messageError && (
            <Shape
              width="100%"
              borderRadius="xl"
              border="1px solid warning"
              backgroundColor="pending100"
              padding="s8"
            >
              {messageError}
            </Shape>
          )}
        </Wrapper>
      </StyledForm>
      <StyledButtonWrapper>
        <Button
          disabled={isLoadingGetDistanceTerminals}
          block
          type="submit"
          variant="primary"
          onClick={
            isGetCurrentLocation
              ? getDistanceTerminalsByGeoLocation
              : getDistanceTerminalsByAddressForm
          }
          width={'100%'}
        >
          {t('Xác nhận')}
        </Button>
      </StyledButtonWrapper>
    </>
  );
};

const Wrapper = styled.div`
  height: 420px;
`;

const SubTitle = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

const StyledSelect = styled(Select)`
  margin-top: 16px;
  input {
    border-radius: 4px;
  }
`;

const StyledForm = styled(Form)`
  width: 100%;
`;

const StyledButtonWrapper = styled(Shape)`
  width: 100%;
  position: fixed;
  bottom: 16px;
  padding-left: 16px;
  padding-right: 16px;
`;

const Error = styled.div(
  ({ theme }: ThemeProps) => `
    padding-top: 0;
    color: ${theme?.colors.error500};
    font-size: 0.75rem;
    line-height: 1rem;
    transition: color 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
    margin-top: 0.5rem;
  `,
);
export const SuggestWrapper = styled.div`
  background-color: #fafafa;
  border-radius: 12px;
  padding: 8px;
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: scroll;
`;

export const ItemSuggestion = styled.div`
  padding: 8px 10px;
  cursor: pointer;
`;

const Text = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #434657;
`;
export const Spinner = styled.div<ThemeProps>(
  ({ theme }) => `
  border: 3px solid ${theme.colors.blue100};
  border-top-color: ${theme.colors.blue500};
  border-radius: 50%;
  width: 1.5em;
  height: 1.5em;
  margin: 0 0.5rem;
  animation: spin 1s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`,
);
